import React from 'react'
import { Box, Typography, Stack } from '@mui/material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import { SEMANTIC } from '../../theme/theme'

/**
 * Écran de verdict après un scan.
 *
 * Le résultat doit être interprétable en moins d'une seconde, à bout de bras,
 * en plein soleil comme de nuit. D'où trois signaux redondants plutôt qu'un
 * seul : la couleur pleine du bandeau, le pictogramme, et le mot. Un employé
 * daltonien ou un écran délavé au soleil laissent toujours deux indices.
 *
 * Le bandeau se dimensionne en `flex` (et non en hauteur fixe) : sur un petit
 * écran il se comprime, sur un grand il respire, et les détails du billet
 * restent visibles sans défilement dans les deux cas.
 */

const TONES = {
  valid: {
    color: SEMANTIC.valid,
    dark: SEMANTIC.validDark,
    surface: SEMANTIC.validSurface,
    Icon: CheckCircleRoundedIcon,
  },
  invalid: {
    color: SEMANTIC.invalid,
    dark: SEMANTIC.invalidDark,
    surface: SEMANTIC.invalidSurface,
    Icon: CancelRoundedIcon,
  },
  warning: {
    color: SEMANTIC.warning,
    dark: SEMANTIC.warningDark,
    surface: SEMANTIC.warningSurface,
    Icon: WarningRoundedIcon,
  },
}

export const VerdictScreen = ({
  status = 'valid',
  title,
  message,
  children,
  footer,
  maxWidth = 560,
}) => {
  const tone = TONES[status] || TONES.valid
  const { Icon } = tone

  return (
    <Box
      sx={{
        height: 'var(--app-vh)',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        overflow: 'hidden',
      }}
    >
      {/* ---------- Bandeau de verdict ---------- */}
      <Box
        sx={{
          flex: '0 0 auto',
          pt: 'calc(var(--safe-top) + 20px)',
          pb: { xs: 2.5, sm: 3.5 },
          px: 3,
          textAlign: 'center',
          background: `linear-gradient(160deg, ${tone.color} 0%, ${tone.dark} 100%)`,
          color: '#FFFFFF',
          // Écran court (téléphone en paysage, petit format) : le bandeau se
          // comprime pour que les informations du billet restent visibles
          // sans avoir à faire défiler.
          '@media (max-height: 520px)': {
            pt: 'calc(var(--safe-top) + 10px)',
            pb: 1.5,
          },
        }}
      >
        <Stack alignItems="center" spacing={1}>
          <Icon
            sx={{
              fontSize: 'clamp(64px, 16vh, 116px)',
              filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.25))',
              '@media (max-height: 520px)': { fontSize: 'clamp(40px, 12vh, 56px)' },
            }}
          />
          <Typography
            variant="h2"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: '-0.02em',
              lineHeight: 1,
              '@media (max-height: 520px)': { fontSize: '1.75rem' },
            }}
          >
            {title}
          </Typography>
          {message ? (
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                opacity: 0.95,
                maxWidth: 460,
                '@media (max-height: 520px)': { fontSize: '0.9375rem' },
              }}
            >
              {message}
            </Typography>
          ) : null}
        </Stack>
      </Box>

      {/* ---------- Détails ---------- */}
      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          pl: 'var(--safe-left)',
          pr: 'var(--safe-right)',
        }}
      >
        <Box sx={{ maxWidth, mx: 'auto', width: '100%', px: { xs: 2, sm: 3 }, py: 2.5 }}>
          {children}
        </Box>
      </Box>

      {/* ---------- Actions ---------- */}
      {footer ? (
        <Box
          sx={{
            flex: '0 0 auto',
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
            pb: 'var(--safe-bottom)',
            pl: 'var(--safe-left)',
            pr: 'var(--safe-right)',
          }}
        >
          <Box sx={{ maxWidth, mx: 'auto', width: '100%', px: { xs: 2, sm: 3 }, py: 1.5 }}>
            {footer}
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}

export default VerdictScreen
